import { html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhVideoCamera";
import "@phosphor-icons/webcomponents/PhVideoCameraSlash";
import "@phosphor-icons/webcomponents/PhMicrophone";
import "@phosphor-icons/webcomponents/PhMicrophoneSlash";
import "@phosphor-icons/webcomponents/PhMonitorArrowUp";
import "@phosphor-icons/webcomponents/PhCameraRotate";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhEyeClosed";
import "@phosphor-icons/webcomponents/PhCheck";
import "ui-components/lucide-icons/circle-pause";
import "ui-components/lucide-icons/circle-stop";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhChat";
import "@phosphor-icons/webcomponents/PhUsers";
import "ui-components/event-badge/event-badge";
import "ui-components/button/wow-button";
import "ui-components/stream-recorder/stream-recorder";
import "ui-components/stream-player/cdn-player";
import "ui-components/live-badge/live-badge";
import "ui-components/go-live-button/go-live-button";
import "ui-components/centered-dialog/wow-centered-dialog";
import "ui-components/feedback/create-feedback-modal";
import "ui-components/live-chat/live-chat";
import "ui-components/collapsible-section/collapsible-section";
import "ui-components/form-elements/select/wow-select";
import "ui-components/stream-quality-indicator/stream-quality-indicator";

import { t, translate } from "../../translations/i18n";
import { NotificationService } from "ui-components/notification";
import { SignalElement, inject, effect } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { ICreatorLiveEventsState, ICreatorLiveEventsService } from "../../services/creator-live-events/types";
import type {
  IBroadcasterDiagnosticsState,
  IBroadcasterDiagnosticsService,
} from "ui-common/services/broadcaster-diagnostics/types";

import { StreamRecorder } from "ui-components/stream-recorder/stream-recorder";
import type { LiveBadgeStatus } from "ui-components/live-badge/live-badge";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";

import { navigateTo } from "../../router";
import { styles } from "./preview-stream-page.css";

type PermissionState = "prompt" | "granted" | "denied" | "checking";

@customElement("preview-stream-page")
export class PreviewStreamPage extends SignalElement {
  static override styles = [styles];

  @inject<ICreatorLiveEventsState>("creatorLiveEventsState")
  private liveEventsState!: ICreatorLiveEventsState;

  @inject<ICreatorLiveEventsService>("creatorLiveEventsService")
  private liveEventsService!: ICreatorLiveEventsService;

  @inject<IBroadcasterDiagnosticsState>("broadcasterDiagnosticsState")
  private diagnosticsState!: IBroadcasterDiagnosticsState;

  @inject<IBroadcasterDiagnosticsService>("broadcasterDiagnosticsService")
  private diagnosticsService!: IBroadcasterDiagnosticsService;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @query("stream-recorder")
  private streamRecorder?: StreamRecorder;

  @state()
  private streamId = "";

  @state()
  private isLive = false;

  @state()
  private eventTitle = "";

  @state()
  private isStreaming = false;

  @state()
  private isPaused = false;

  @state()
  private isConnecting = false;

  @state()
  private streamingDuration = 0;

  @state()
  private errorMessage = "";

  @state()
  private hasMediaAccessError = false;

  @state()
  private showLeaveConfirmation = false;

  @state()
  private showEndLiveConfirmation = false;

  @state()
  private showFeedbackModal = false;

  private leaveConfirmResolver?: (result: boolean) => void;

  @state()
  private isEnded = false;

  @state()
  private isStopping = false;

  @state()
  private isScreenSharing = false;

  @state()
  private showStreamKey = false;

  @state()
  private isScreenShareSupported = false;

  @state()
  private hasMultipleCameras = false;

  @state()
  private isCameraOn = true;

  @state()
  private isMicOn = true;

  @state()
  private isChatVisible = true;

  @state()
  private viewerCount = 0;

  @state()
  private streamSource: "webcam" | "obs" = "webcam";

  @state()
  private chatTab: "settings" | "chat" = "settings";

  @state()
  private cameraPermission: PermissionState = "checking";

  private durationInterval: number | null = null;
  private viewerCountInterval: number | null = null;
  private cleanupEffect: (() => void) | null = null;
  private boundDebugKeyHandler = this.handleDebugKeydown.bind(this);

  override connectedCallback() {
    super.connectedCallback();
    this.isChatVisible = window.innerWidth > 768;
    this.isScreenShareSupported = StreamRecorder.isScreenShareSupported();
    void StreamRecorder.hasMultipleCameras().then((v) => (this.hasMultipleCameras = v));
    window.addEventListener("keydown", this.boundDebugKeyHandler);
    this.diagnosticsService.setToastHandler((type, message) => {
      if (type === "warning") {
        NotificationService.warning(message);
      } else {
        NotificationService.success(message);
      }
    });

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const segmentIndex = pathSegments.indexOf("stream");
    const eventId = segmentIndex !== -1 ? pathSegments[segmentIndex + 1] : undefined;

    if (eventId) {
      this.streamId = eventId;
      this.cleanupEffect = effect(() => {
        this.liveEventsState.events.get();
        this.loadEventDetails();
      });
    } else {
      navigateTo("live-events");
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this.boundDebugKeyHandler);
    this.cleanupEffect?.();
    this.stopDurationTimer();
    this.stopViewerCountPolling();
  }

  override firstUpdated() {
    if (this.streamId) {
      void this.startStream();
    }
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    super.updated(changed);
    if (changed.has("streamSource") && this.streamSource === "webcam" && !this.isStreaming) {
      void this.updateComplete.then(() => this.startStream());
    }
  }

  private loadEventDetails(): void {
    const event = this.getEvent();
    if (event) {
      this.eventTitle = event.title;
      this.isLive = event.status === "live";
      this.isEnded = event.status === "ended";
      this.streamSource = event.stream_delivery === "obs" ? "obs" : "webcam";
      if (this.streamSource === "webcam") {
        void this.checkCameraPermission();
      }
      if (this.streamSource === "obs" && !this.isStreaming) {
        this.isStreaming = true;
      }
      if (this.isLive) {
        this.chatTab = "chat";
        this.isChatVisible = !this.isMobilePortrait();
        this.startDurationTimer();
        this.startViewerCountPolling();
      }
    }
  }

  private getEvent(): LiveEvent | undefined {
    const events = this.liveEventsState.events.get();
    return events.find((e) => e.id === this.streamId);
  }

  private get RTMP_URL(): string {
    const isLocalhost = (location.origin ?? "localhost").includes("localhost");
    const isProduction = location.origin.includes("worldofwow.com");
    const liveDomain = isLocalhost
      ? "live.worldofwow.dev"
      : isProduction
        ? "live.worldofwow.com"
        : "live.worldofwow.dev";
    return `rtmp://${liveDomain}/streams`;
  }

  private handleReportProblem(e: Event): void {
    e.preventDefault();
    this.showFeedbackModal = true;
  }

  private async copyToClipboard(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      NotificationService.success(`${label} copied to clipboard`);
    } catch {
      NotificationService.error("Failed to copy");
    }
  }

  private async checkCameraPermission(): Promise<void> {
    try {
      this.cameraPermission = "checking";
      const result = await navigator.permissions.query({ name: "camera" as PermissionName });
      this.cameraPermission = result.state as PermissionState;
      result.addEventListener("change", () => {
        this.cameraPermission = result.state as PermissionState;
      });
    } catch {
      this.cameraPermission = "prompt";
    }
  }

  private handleStreamSourceChange(e: CustomEvent<{ value: "webcam" | "obs" }>): void {
    const newSource = e.detail.value;

    if (this.streamSource === "webcam" && newSource === "obs") {
      if (this.streamRecorder) {
        void this.streamRecorder.stopStreaming();
      }
    }

    this.streamSource = newSource;
    this.isStreaming = false;
    this.stopDurationTimer();
    this.isCameraOn = true;
    this.isMicOn = true;
    this.isScreenSharing = false;

    if (newSource === "webcam") {
      void this.checkCameraPermission();
    }

    this.liveEventsService.updateEventLocally(this.streamId, {
      stream_delivery: newSource === "obs" ? "obs" : "browser",
    });
    void this.saveStreamSource(newSource);
  }

  private async saveStreamSource(source: "webcam" | "obs"): Promise<void> {
    if (!this.streamId) return;
    try {
      await this.rpcService.call("update-live-event", {
        event_id: this.streamId,
        stream_delivery: source === "obs" ? "obs" : "browser",
      });
    } catch {
      // Source save failure is non-critical; stream can still work locally
    }
  }

  private async startStream(): Promise<void> {
    if (this.streamSource !== "webcam") return;
    if (!this.streamRecorder) return;

    this.isConnecting = true;
    this.errorMessage = "";

    try {
      await this.streamRecorder.startStreaming();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : "Failed to start streaming";
      this.isConnecting = false;
    }
  }

  private handleTelemetrySnapshot(e: CustomEvent): void {
    this.diagnosticsService.feedSnapshot(e.detail);
  }

  private handleStreamingStarted(): void {
    this.isStreaming = true;
    this.isConnecting = false;
    this.hasMediaAccessError = false;
    // Re-detect after camera permission is granted (enumerateDevices returns full list)
    void StreamRecorder.hasMultipleCameras().then((v) => (this.hasMultipleCameras = v));
  }

  private handleStreamingStopped(): void {
    this.isStreaming = false;
    this.isPaused = false;
    this.diagnosticsService.reset();
  }

  private handleStreamingError(e: CustomEvent): void {
    const kind = e.detail?.kind;
    // Media access failures render inline via stream-recorder's own panel.
    const isMediaAccessFailure = kind === "permission-denied" || kind === "no-device" || kind === "device-busy";
    this.errorMessage = isMediaAccessFailure ? "" : e.detail?.message || "Streaming error occurred";
    this.hasMediaAccessError = isMediaAccessFailure;
    this.isConnecting = false;
    this.isStreaming = false;
  }

  private handleMediaAccessClosed = (): void => {
    this.hasMediaAccessError = false;
  };

  private handleStreamingPaused(): void {
    this.isPaused = true;
  }

  private handleStreamingResumed(): void {
    this.isPaused = false;
  }

  private startDurationTimer(): void {
    this.stopDurationTimer();
    const event = this.getEvent();
    const startTime = event?.actual_start_time ? new Date(event.actual_start_time).getTime() : null;
    if (!startTime) return;

    this.streamingDuration = Math.floor((Date.now() - startTime) / 1000);
    this.durationInterval = window.setInterval(() => {
      this.streamingDuration = Math.floor((Date.now() - startTime) / 1000);
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval !== null) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private startViewerCountPolling(): void {
    this.stopViewerCountPolling();
    if (!this.streamId) return;
    void this.fetchViewerCount();
    this.viewerCountInterval = window.setInterval(() => void this.fetchViewerCount(), 10_000);
  }

  private stopViewerCountPolling(): void {
    if (this.viewerCountInterval !== null) {
      clearInterval(this.viewerCountInterval);
      this.viewerCountInterval = null;
    }
  }

  private async fetchViewerCount(): Promise<void> {
    if (!this.streamId) return;
    try {
      const response = await fetch(`/api/viewer-count/${this.streamId}`);
      if (response.ok) {
        const data = (await response.json()) as { count: number };
        this.viewerCount = data.count;
      }
    } catch {
      // Viewer count is non-critical
    }
  }

  private formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  private handlePauseResume(): void {
    if (!this.streamRecorder) return;

    if (this.isPaused) {
      this.streamRecorder.resumeStreaming();
    } else {
      this.streamRecorder.pauseStreaming();
    }
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isEnded) return true;
    return new Promise((resolve) => {
      this.leaveConfirmResolver = resolve;
      this.showLeaveConfirmation = true;
    });
  }

  private handleClose(): void {
    navigateTo("live-events");
  }

  private async handleConfirmLeave(): Promise<void> {
    this.showLeaveConfirmation = false;

    if (this.streamSource === "webcam" && this.isStreaming) {
      if (this.streamRecorder) {
        void this.streamRecorder.stopStreaming();
      }
    }

    this.liveEventsService.refetch();

    if (this.leaveConfirmResolver) {
      this.leaveConfirmResolver(true);
      this.leaveConfirmResolver = undefined;
    } else {
      navigateTo("live-events");
    }
  }

  private handleCancelLeave(): void {
    this.showLeaveConfirmation = false;
    if (this.leaveConfirmResolver) {
      this.leaveConfirmResolver(false);
      this.leaveConfirmResolver = undefined;
    }
  }

  private handleEventWentLive(e: Event): void {
    e.stopPropagation();
    this.isLive = true;
    this.chatTab = "chat";
    this.isChatVisible = !this.isMobilePortrait();
    this.startViewerCountPolling();
    this.startDurationTimer();
  }

  private handleEndLiveClick(): void {
    this.showEndLiveConfirmation = true;
  }

  private handleCancelEndLive(): void {
    this.showEndLiveConfirmation = false;
  }

  private async handleConfirmEndLive(): Promise<void> {
    if (!this.streamId) return;

    try {
      this.isStopping = true;
      this.showEndLiveConfirmation = false;
      const result = await this.rpcService.call<{ success: boolean }>("end-live-event", { event_id: this.streamId });

      if (result.success) {
        NotificationService.success(t("previewStreamPage.stopStreamSuccess") as string);
        this.isEnded = true;
        this.stopViewerCountPolling();
        this.liveEventsService.refetch();
        navigateTo("analytics", true, ["live-events"]);
      } else {
        NotificationService.error(t("previewStreamPage.stopStreamFailed") as string);
      }
    } catch (error) {
      console.error("Error stopping live event:", error);
      NotificationService.error(t("previewStreamPage.stopStreamFailed") as string);
    } finally {
      this.isStopping = false;
    }
  }

  private handleToggleScreenShare(): void {
    if (!this.streamRecorder) return;

    if (this.isScreenSharing) {
      void this.streamRecorder.stopScreenShare();
    } else {
      void this.streamRecorder.startScreenShare();
    }
  }

  private handleScreenShareStarted(): void {
    this.isScreenSharing = true;
  }

  private handleScreenShareStopped(): void {
    this.isScreenSharing = false;
  }

  private handleScreenShareError(e: CustomEvent): void {
    console.error("Screen share error:", e.detail?.message);
    this.isScreenSharing = false;
  }

  private handleToggleCamera(): void {
    if (!this.streamRecorder) return;
    this.streamRecorder.toggleCamera();
  }

  private handleCameraEnabled(): void {
    this.isCameraOn = true;
  }

  private handleCameraDisabled(): void {
    this.isCameraOn = false;
  }

  private handleToggleMicrophone(): void {
    if (!this.streamRecorder) return;
    this.streamRecorder.toggleMicrophone();
  }

  private handleMicrophoneEnabled(): void {
    this.isMicOn = true;
  }

  private handleMicrophoneDisabled(): void {
    this.isMicOn = false;
  }

  private handleSwitchCamera(): void {
    if (!this.streamRecorder) return;
    void this.streamRecorder.switchCamera();
  }

  private isMobilePortrait(): boolean {
    return window.innerWidth <= 768 && window.matchMedia("(orientation: portrait)").matches;
  }

  private handleCloseChat(): void {
    this.isChatVisible = false;
  }

  private handleOpenChat(): void {
    this.isChatVisible = true;
    this.chatTab = "chat";
  }

  private handleDebugKeydown(e: KeyboardEvent): void {
    if (e.ctrlKey && e.shiftKey && e.key === "D") {
      e.preventDefault();
      this.diagnosticsService.toggleDebugOverlay();
    }
  }

  private renderDebugOverlay() {
    if (!this.diagnosticsState.showDebugOverlay.get()) return nothing;
    const snapshot = this.diagnosticsState.latestSnapshot.get();
    if (!snapshot) return nothing;

    return html`
      <div class="debug-overlay">
        <div class="debug-section">
          <div>Score: ${snapshot.qualityScore} (${this.diagnosticsState.qualityRating.get()})</div>
          <div>Bitrate: ${snapshot.videoBitrateSent.toFixed(0)} kbps</div>
          <div>FPS: ${snapshot.framesPerSecond}</div>
          <div>Resolution: ${snapshot.frameWidth}x${snapshot.frameHeight}</div>
          <div>RTT: ${snapshot.currentRtt.toFixed(0)} ms</div>
          <div>Jitter: ${snapshot.remoteJitter.toFixed(1)} ms</div>
          <div>Pkt Loss: ${(snapshot.remoteFractionLost * 100).toFixed(2)}%</div>
          <div>ICE: ${snapshot.iceConnectionState}</div>
          ${snapshot.qualityLimitationReason ? html`<div>QL: ${snapshot.qualityLimitationReason}</div>` : nothing}
          <div>Audio: ${snapshot.audioBitrateSent.toFixed(0)} kbps</div>
          <div>Video Codec: ${snapshot.videoCodec}</div>
          <div>Audio Codec: ${snapshot.audioCodec}</div>
          ${snapshot.encoderImplementation ? html`<div>Encoder: ${snapshot.encoderImplementation}</div>` : nothing}
          ${snapshot.availableOutgoingBitrate != null
            ? html`<div>Avail BW: ${(snapshot.availableOutgoingBitrate / 1000).toFixed(0)} kbps</div>`
            : nothing}
          <div>NACKs: ${snapshot.nackCount}</div>
          <div>Retransmit: ${snapshot.retransmittedPacketsSent}</div>
        </div>
      </div>
    `;
  }

  private getBadgeStatus(): LiveBadgeStatus {
    if (this.isPaused) return "paused";
    if (this.isLive) return "live";
    return "preview";
  }

  private getBadgeLabel(): string {
    if (this.isPaused) return t("liveStreamModal.paused") as string;
    if (this.isLive) return t("liveStreamModal.live") as string;
    return t("liveStreamModal.preview") as string;
  }

  private renderHeader() {
    return html`
      <div class="page-header">
        <wow-button class="close-btn" variant="neutral" size="small" square rounded @click=${this.handleClose}>
          <ph-x size="18"></ph-x>
        </wow-button>
        ${this.streamSource === "obs"
          ? html`<span class="obs-hint">${t("previewStreamPage.obsStreamHint")}</span>`
          : nothing}
      </div>
    `;
  }

  private renderMobileSideControls() {
    return html`
      <div class="mobile-side-controls">
        <wow-button
          variant=${this.isCameraOn ? "neutral" : "danger"}
          size="small"
          square
          rounded
          @click=${this.handleToggleCamera}
        >
          ${this.isCameraOn
            ? html`<ph-video-camera size="18"></ph-video-camera>`
            : html`<ph-video-camera-slash size="18"></ph-video-camera-slash>`}
        </wow-button>
        ${this.hasMultipleCameras
          ? html`
              <wow-button variant="neutral" size="small" square rounded @click=${this.handleSwitchCamera}>
                <ph-camera-rotate size="18"></ph-camera-rotate>
              </wow-button>
            `
          : nothing}
        <wow-button
          variant=${this.isMicOn ? "neutral" : "danger"}
          size="small"
          square
          rounded
          @click=${this.handleToggleMicrophone}
        >
          ${this.isMicOn
            ? html`<ph-microphone size="18"></ph-microphone>`
            : html`<ph-microphone-slash size="18"></ph-microphone-slash>`}
        </wow-button>
        <wow-button
          variant=${this.isChatVisible ? "primary" : "neutral"}
          size="small"
          rounded
          @click=${this.isChatVisible ? this.handleCloseChat : this.handleOpenChat}
        >
          <ph-chat size="18" weight=${this.isChatVisible ? "fill" : "regular"}></ph-chat>
          ${t("previewStreamPage.chat")}
        </wow-button>
      </div>
    `;
  }

  private renderLiveControls() {
    return html`
      <div class="preview-controls">
        <wow-button
          variant=${this.isCameraOn ? "neutral" : "danger"}
          size="small"
          square
          rounded
          @click=${this.handleToggleCamera}
        >
          ${this.isCameraOn
            ? html`<ph-video-camera size="18"></ph-video-camera>`
            : html`<ph-video-camera-slash size="18"></ph-video-camera-slash>`}
        </wow-button>
        <wow-button
          variant=${this.isMicOn ? "neutral" : "danger"}
          size="small"
          square
          rounded
          @click=${this.handleToggleMicrophone}
        >
          ${this.isMicOn
            ? html`<ph-microphone size="18"></ph-microphone>`
            : html`<ph-microphone-slash size="18"></ph-microphone-slash>`}
        </wow-button>
        ${this.isScreenShareSupported
          ? html`
              <wow-button
                variant=${this.isScreenSharing ? "primary" : "neutral"}
                size="small"
                square
                rounded
                @click=${this.handleToggleScreenShare}
              >
                <ph-monitor-arrow-up
                  size="18"
                  weight=${this.isScreenSharing ? "fill" : "regular"}
                ></ph-monitor-arrow-up>
              </wow-button>
            `
          : nothing}
      </div>
    `;
  }

  private renderLiveFooter() {
    const isWebcam = this.streamSource === "webcam";

    return html`
      <div class="preview-footer live-footer">
        ${isWebcam
          ? html`
              <!-- Row 1: device controls (mobile, webcam only) -->
              <div class="live-footer-row live-footer-devices mobile-only">
                <wow-button
                  variant=${this.isCameraOn ? "neutral" : "danger"}
                  size="small"
                  square
                  rounded
                  @click=${this.handleToggleCamera}
                >
                  ${this.isCameraOn
                    ? html`<ph-video-camera size="18"></ph-video-camera>`
                    : html`<ph-video-camera-slash size="18"></ph-video-camera-slash>`}
                </wow-button>
                ${this.hasMultipleCameras
                  ? html`
                      <wow-button variant="neutral" size="small" square rounded @click=${this.handleSwitchCamera}>
                        <ph-camera-rotate size="18"></ph-camera-rotate>
                      </wow-button>
                    `
                  : nothing}
                <wow-button
                  variant=${this.isMicOn ? "neutral" : "danger"}
                  size="small"
                  square
                  rounded
                  @click=${this.handleToggleMicrophone}
                >
                  ${this.isMicOn
                    ? html`<ph-microphone size="18"></ph-microphone>`
                    : html`<ph-microphone-slash size="18"></ph-microphone-slash>`}
                </wow-button>
                <wow-button
                  variant=${this.isChatVisible ? "primary" : "neutral"}
                  size="small"
                  rounded
                  @click=${this.isChatVisible ? this.handleCloseChat : this.handleOpenChat}
                >
                  <ph-chat size="18" weight=${this.isChatVisible ? "fill" : "regular"}></ph-chat>
                  ${t("previewStreamPage.chat")}
                </wow-button>
                ${this.isScreenShareSupported
                  ? html`
                      <wow-button
                        variant=${this.isScreenSharing ? "primary" : "neutral"}
                        size="small"
                        square
                        rounded
                        @click=${this.handleToggleScreenShare}
                      >
                        <ph-monitor-arrow-up
                          size="18"
                          weight=${this.isScreenSharing ? "fill" : "regular"}
                        ></ph-monitor-arrow-up>
                      </wow-button>
                    `
                  : nothing}
              </div>
            `
          : nothing}

        <!-- Row 2: info (badge, quality, duration, viewers, chat, title) -->
        <div class="preview-footer-info">
          <live-badge bars status=${this.getBadgeStatus()} label=${this.getBadgeLabel()}></live-badge>
          <stream-quality-indicator></stream-quality-indicator>
          <span class="duration">${this.formatDuration(this.streamingDuration)}</span>
          <event-badge hide-dot label=${this.viewerCount.toString()}>
            <ph-users slot="icon" size="16" weight="regular"></ph-users>
          </event-badge>
          ${!this.isChatVisible && !isWebcam
            ? html`
                <wow-button
                  class="mobile-only"
                  variant="neutral"
                  size="small"
                  square
                  rounded
                  @click=${this.handleOpenChat}
                >
                  <ph-chat size="18" weight="fill"></ph-chat>
                </wow-button>
              `
            : nothing}
          ${this.eventTitle ? html`<span class="preview-footer-title">${this.eventTitle}</span>` : nothing}
        </div>

        <!-- Row 3: stream actions (mobile) -->
        <div class="live-footer-row live-footer-actions-mobile mobile-only">
          ${isWebcam && this.isStreaming
            ? html`
                <wow-button variant="neutral" size="small" rounded @click=${this.handlePauseResume}>
                  <lu-circle-pause size="18"></lu-circle-pause>
                  ${this.isPaused ? t("liveStreamModal.resume") : t("liveStreamModal.pause")}
                </wow-button>
              `
            : nothing}
          ${this.isLive
            ? html`
                <wow-button
                  variant="danger"
                  size="small"
                  rounded
                  @click=${this.handleEndLiveClick}
                  ?disabled=${this.isStopping}
                >
                  <lu-circle-stop size="18"></lu-circle-stop>
                  ${t("previewStreamPage.endLiveConfirm")}
                </wow-button>
              `
            : nothing}
        </div>

        <!-- Desktop actions -->
        <div class="live-footer-actions desktop-only">
          ${isWebcam && this.isStreaming
            ? html`
                <wow-button variant="neutral" size="small" rounded @click=${this.handlePauseResume}>
                  <lu-circle-pause size="18"></lu-circle-pause>
                  ${this.isPaused ? t("liveStreamModal.resume") : t("liveStreamModal.pause")}
                </wow-button>
              `
            : nothing}
          ${this.isLive
            ? html`
                <wow-button
                  variant="danger"
                  size="small"
                  rounded
                  @click=${this.handleEndLiveClick}
                  ?disabled=${this.isStopping}
                >
                  <lu-circle-stop size="18"></lu-circle-stop>
                  ${t("previewStreamPage.endLiveConfirm")}
                </wow-button>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderPermissionStatus() {
    const statusClasses = {
      "permission-status": true,
      [this.cameraPermission]: true,
    };

    switch (this.cameraPermission) {
      case "checking":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-video-camera size="20"></ph-video-camera>
            <span>${t("streamConfigModal.checkingPermissions")}</span>
          </div>
        `;
      case "granted":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-check size="20" weight="bold"></ph-check>
            <span>${t("streamConfigModal.cameraAccessGranted")}</span>
          </div>
        `;
      case "denied":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-x size="20" weight="bold"></ph-x>
            <span>${t("streamConfigModal.cameraAccessDenied")}</span>
          </div>
          <p class="permission-instructions">${t("streamConfigModal.cameraAccessDeniedInstructions")}</p>
        `;
      case "prompt":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-warning size="20"></ph-warning>
            <span>${t("streamConfigModal.cameraPermissionPrompt")}</span>
          </div>
        `;
    }
  }

  private renderObsConfig() {
    if (!this.streamId) return nothing;

    return html`
      <div class="obs-config-content">
        <div class="obs-steps">
          <div class="obs-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.obsStepConnect")}</div>
              <div class="step-description">${t("createLiveEventPage.obsStep1")}</div>
            </div>
          </div>

          <div class="obs-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.serverUrl")}</div>

              <div class="obs-credential-row">
                <code class="obs-credential-value">${this.RTMP_URL}</code>
                <wow-button
                  variant="subtle"
                  size="small"
                  square
                  @click=${() => this.copyToClipboard(this.RTMP_URL, "URL")}
                >
                  <ph-copy size="16"></ph-copy>
                </wow-button>
              </div>

              <div class="step-title">${t("createLiveEventPage.streamKey")}</div>

              <div class="obs-credential-row">
                <div class="obs-credential-value-wrapper">
                  <code class="obs-credential-value ${this.showStreamKey ? "" : "masked"}"
                    >${this.showStreamKey ? this.streamId : "•".repeat(40)}</code
                  >
                  <wow-button
                    class="stream-key-toggle"
                    variant="subtle"
                    size="small"
                    square
                    @click=${() => (this.showStreamKey = !this.showStreamKey)}
                  >
                    ${this.showStreamKey
                      ? html`<ph-eye-closed size="16"></ph-eye-closed>`
                      : html`<ph-eye size="16"></ph-eye>`}
                  </wow-button>
                </div>
                <wow-button
                  variant="subtle"
                  size="small"
                  square
                  @click=${() => this.copyToClipboard(this.streamId, "Stream Key")}
                >
                  <ph-copy size="16"></ph-copy>
                </wow-button>
              </div>
            </div>
          </div>

          <div class="obs-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.obsStepCheck")}</div>
              <div class="step-description">
                ${t("createLiveEventPage.obsStep3")}
                <a href="#" class="problem-link" @click=${this.handleReportProblem}
                  >${t("createLiveEventPage.reportProblem")}</a
                >.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderStreamSettingsContent() {
    return html`
      <wow-collapsible-section
        collapsible
        expanded
        header=${t("createLiveEventPage.chooseStreamSource")}
        subtitle=${t("createLiveEventPage.chooseStreamSourceSubtitle")}
      >
        <div class="stream-settings-container">
          <wow-select
            label=${t("createLiveEventPage.selectSource")}
            .options=${[
              { value: "webcam", label: translate("createLiveEventPage.computerWebcam") },
              { value: "obs", label: "OBS Studio" },
            ]}
            .value=${this.streamSource}
            @change=${this.handleStreamSourceChange}
          ></wow-select>
          ${this.streamSource === "webcam"
            ? html`<div class="form-section">${this.renderPermissionStatus()}</div>`
            : this.renderObsConfig()}
        </div>
      </wow-collapsible-section>
    `;
  }

  override render() {
    return html`
      <div class="streaming-content">
        <div class="live-content${this.isLive ? " is-live" : ""}">
          <div class="video-section">
            ${this.renderHeader()}
            ${this.errorMessage
              ? html`
                  <div class="error-banner">
                    <ph-warning size="20"></ph-warning>
                    <span>${this.errorMessage}</span>
                  </div>
                `
              : nothing}
            ${this.isConnecting && !this.isStreaming
              ? html`
                  <div class="connecting-state">
                    <div class="spinner"></div>
                    <span>${t("liveStreamModal.startingStream")}</span>
                  </div>
                `
              : nothing}
            <div class="video-container">
              ${this.streamSource === "webcam"
                ? html`
                    <stream-recorder
                      .streamId=${this.streamId}
                      .mediaAccessTranslations=${{
                        permissionDeniedHeading: translate("mediaAccessPanel.permissionDeniedHeading"),
                        permissionDeniedBody: translate("mediaAccessPanel.permissionDeniedBody"),
                        noDeviceHeading: translate("mediaAccessPanel.noDeviceHeading"),
                        noDeviceBody: translate("mediaAccessPanel.noDeviceBody"),
                        deviceBusyHeading: translate("mediaAccessPanel.deviceBusyHeading"),
                        deviceBusyBody: translate("mediaAccessPanel.deviceBusyBody"),
                        retry: translate("mediaAccessPanel.retry"),
                        close: translate("mediaAccessPanel.close"),
                      }}
                      telemetry-mode="remote"
                      enable-audio
                      show-preview
                      hide-controls
                      @streaming-started=${this.handleStreamingStarted}
                      @streaming-stopped=${this.handleStreamingStopped}
                      @streaming-error=${this.handleStreamingError}
                      @streaming-paused=${this.handleStreamingPaused}
                      @streaming-resumed=${this.handleStreamingResumed}
                      @screen-share-started=${this.handleScreenShareStarted}
                      @screen-share-stopped=${this.handleScreenShareStopped}
                      @screen-share-error=${this.handleScreenShareError}
                      @telemetry-snapshot=${this.handleTelemetrySnapshot}
                      @camera-enabled=${this.handleCameraEnabled}
                      @camera-disabled=${this.handleCameraDisabled}
                      @microphone-enabled=${this.handleMicrophoneEnabled}
                      @microphone-disabled=${this.handleMicrophoneDisabled}
                      @media-access-closed=${this.handleMediaAccessClosed}
                    ></stream-recorder>
                  `
                : html`<cdn-player .streamId=${this.streamId}></cdn-player>`}
              ${this.isStreaming && this.streamSource === "webcam" ? this.renderLiveControls() : nothing}
              ${this.isStreaming && !this.isLive && this.streamSource === "webcam"
                ? this.renderMobileSideControls()
                : nothing}
              ${this.isStreaming ? this.renderDebugOverlay() : nothing}
            </div>
            ${this.isLive ? this.renderLiveFooter() : nothing}
            ${!this.isLive && !this.isEnded
              ? html`
                  <div class="page-footer">
                    <div class="preview-footer-info">
                      <live-badge bars status=${this.getBadgeStatus()} label=${this.getBadgeLabel()}></live-badge>
                      ${this.isStreaming ? html`<stream-quality-indicator></stream-quality-indicator>` : nothing}
                      <span class="duration">${this.formatDuration(this.streamingDuration)}</span>
                      ${this.eventTitle ? html`<span class="preview-footer-title">${this.eventTitle}</span>` : nothing}
                    </div>
                    <div class="go-live-wrapper">
                      <go-live-button
                        .eventId=${this.streamId}
                        variant="danger"
                        size="small"
                        rounded
                        ?disabled=${this.streamSource === "webcam" && this.hasMediaAccessError}
                        .translations=${{
                          goLive: translate("previewStreamPage.startStream"),
                          goingLive: translate("editLiveEventModal.goingLive"),
                          goLiveSuccess: translate("editLiveEventModal.goLiveSuccess"),
                          goLiveFailed: translate("editLiveEventModal.goLiveFailed"),
                          goLiveHeader: translate("editLiveEventModal.goLiveHeader"),
                          goLiveConfirm: translate("editLiveEventModal.goLiveConfirm"),
                          goLiveButton: translate("editLiveEventModal.goLiveButton"),
                          goLiveCancel: translate("editLiveEventModal.goLiveCancel"),
                        }}
                        @event-went-live=${this.handleEventWentLive}
                      ></go-live-button>
                    </div>
                  </div>
                `
              : nothing}
            ${this.isEnded
              ? html`
                  <div class="page-footer">
                    <div class="preview-footer-info">
                      ${this.eventTitle ? html`<span class="preview-footer-title">${this.eventTitle}</span>` : nothing}
                    </div>
                    <wow-button
                      variant="primary"
                      size="small"
                      rounded
                      @click=${() => navigateTo("analytics", true, ["live-events"])}
                    >
                      ${t("liveEventsPage.viewResults")}
                    </wow-button>
                  </div>
                `
              : nothing}
          </div>
          <div class="chat-panel ${!this.isChatVisible ? "chat-hidden" : ""}">
            <h3 class="chat-panel-heading">
              ${this.isLive ? t("previewStreamPage.headingLive") : t("previewStreamPage.headingPreview")}
            </h3>
            <div class="chat-panel-tab-bar">
              <button
                class=${classMap({ active: this.chatTab === "chat" })}
                @click=${() => {
                  this.chatTab = "chat";
                }}
              >
                ${t("liveStreamModal.liveChat")}
              </button>
              <button
                class=${classMap({ active: this.chatTab === "settings" })}
                ?disabled=${this.isLive}
                @click=${() => {
                  this.chatTab = "settings";
                }}
              >
                ${t("liveStreamModal.streamSetup")}
              </button>
            </div>
            <div class="chat-tab-content">
              ${this.chatTab === "settings"
                ? this.renderStreamSettingsContent()
                : html`
                    <live-chat
                      hide-header-top
                      event-id=${this.streamId}
                      event-status=${this.isLive ? "live" : "scheduled"}
                      is-authenticated
                      is-lobby-open
                      show-close-button
                      hide-viewer-count
                      banner-title=${t("liveStreamModal.bannerTitle")}
                      banner-subtitle=${t("liveStreamModal.bannerSubtitle")}
                      @close-chat=${this.handleCloseChat}
                    ></live-chat>
                  `}
            </div>
          </div>
        </div>
      </div>

      ${this.showLeaveConfirmation
        ? html`
            <wow-centered-dialog
              header="${t("previewStreamPage.leaveConfirmHeader")}"
              description="${this.isLive
                ? t("previewStreamPage.leaveConfirmDescriptionLive")
                : t("previewStreamPage.leaveConfirmDescription")}"
              @close=${this.handleCancelLeave}
            >
              <wow-button
                variant="danger"
                size="large"
                full-width
                @click=${this.handleConfirmLeave}
                ?disabled=${this.isStopping}
              >
                ${t("previewStreamPage.leaveConfirmLeave")}
              </wow-button>
              <wow-button variant="neutral" size="large" full-width @click=${this.handleCancelLeave}>
                ${t("previewStreamPage.leaveConfirmStay")}
              </wow-button>
            </wow-centered-dialog>
          `
        : nothing}
      ${this.showEndLiveConfirmation
        ? html`
            <wow-centered-dialog
              header="${t("previewStreamPage.endLiveHeader")}"
              description="${t("previewStreamPage.endLiveDescription")}"
              @close=${this.handleCancelEndLive}
            >
              <wow-button
                variant="danger"
                size="large"
                full-width
                @click=${this.handleConfirmEndLive}
                ?disabled=${this.isStopping}
              >
                <lu-circle-stop slot="prefix" size="18"></lu-circle-stop>
                ${t("previewStreamPage.endLiveConfirm")}
              </wow-button>
              <wow-button variant="neutral" size="large" full-width @click=${this.handleCancelEndLive}>
                ${t("previewStreamPage.endLiveCancel")}
              </wow-button>
            </wow-centered-dialog>
          `
        : nothing}
      ${this.showFeedbackModal
        ? html`<create-feedback-modal
            variant="label"
            headerText=${t("feedback.headerText")}
            sentimentMessage=${t("feedback.sentimentMessage", { context: "post" })}
            sentimentPrompt=${t("feedback.sentimentPrompt")}
            labelPrompt=${t("feedback.labelPrompt")}
            labelPlaceholder=${t("feedback.labelPlaceholder")}
            labelError=${t("feedback.labelError")}
            successMessage=${t("feedback.successMessage")}
            footerButtonText=${t("feedback.footerButtonText")}
            footerButtonLoadingText=${t("feedback.footerButtonLoadingText")}
            @close=${() => (this.showFeedbackModal = false)}
          ></create-feedback-modal>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "preview-stream-page": PreviewStreamPage;
  }
}
